<template>
  <div>
    <b-modal
      id="violation-triggers"
      title="Violation Actions"
      hide-footer
      @hidden="resetTriggersModalForm"
    >
      <b-spinner
        v-if="fetchingTriggers"
        variant="primary"
      />
      <template v-else>
        <template v-if="availableTriggers.length">
          <b-form-radio-group
            v-model="violationActionForm.action"
            :options="availableTriggers"
            class="mb-1"
          />
          <b-form-textarea
            v-model="violationActionForm.description"
            placeholder="Enter Penalty"
            class="mb-1"
          />
          <b-button
            block
            variant="primary"
            :disabled="!violationActionForm.description || !violationActionForm.action || applyingAction"
            @click="applyActionOnViolationHandler"
          >
            <b-spinner
              v-if="applyingAction"
              small
            />
            <span v-else>Apply Action</span>
          </b-button>
        </template>
        <p
          v-else
          class="text-danger"
        >
          No actions available for this violation
        </p>
      </template>
    </b-modal>
    <b-card>
      <b-row>
        <b-col md="4">
          <b-form-group label="Search by state">
            <div class="d-flex">
              <b-form-select
                v-model="filterValues.state"
                :options="stateOptions"
                value-field="value"
                text-field="label"
              />
              <b-button
                v-if="filterValues.state"
                class="ml-1"
                size="sm"
                variant="outline-danger"
                @click="resetStateFilter"
              >
                X
              </b-button>
            </div>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Search by violation type">
            <div class="d-flex">
              <b-form-select
                v-model="filterValues.violation_type"
                :options="violationTypes"
                value-field="id"
                text-field="name"
                :disabled="loadingViolationTypes"
              />
              <b-button
                v-if="filterValues.violation_type"
                class="ml-1"
                size="sm"
                variant="outline-danger"
                @click="resetViolationTypeFilter"
              >
                X
              </b-button>
            </div>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Search by source ID">
            <b-form-input
              v-model="filterValues.description"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="4"
          class="mb-1"
        >
          <b-button
            variant="primary"
            @click="fetchViolations"
          >
            Search
          </b-button>
        </b-col>
      </b-row>
      <b-overlay
        variant="white"
        spinner-variant="primary"
        blur="0"
        opacity=".75"
        rounded="sm"
        :show="loading"
      >
        <b-table
          fixed
          :fields="tableColumns"
          :items="violations"
          class="mb-2 text-center"
          show-empty
          empty-text="Search in order to view drivers violations"
        >
          <template #cell(actions)="data">
            <b-dropdown
              v-if="$can('write', 'drivers-violations')"
              variant="link"
            >

              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>

              <b-dropdown-item @click="viewViolationTriggers(data.item)">
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">Violation Actions</span>
              </b-dropdown-item>
              <b-dropdown-item @click="viewDriver(data.item)">
                <feather-icon icon="EyeIcon" />
                <span class="align-middle ml-50">View Driver</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
        <base-pagination
          v-model="filterValues.page"
          class="mt-1"
          :total="totalViolations"
          @input="fetchViolations"
          @new-per-page="filterValues.per_page = $event;filterValues.page = 1"
        />
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api'
import {
  BTable, BCard, BOverlay, BDropdown, BDropdownItem, BFormGroup, BRow, BCol, BButton, BFormSelect, BFormTextarea, BSpinner, BFormRadioGroup,
  BFormInput,
} from 'bootstrap-vue'
import formatDate from '@/utils/date-format'
import useRegisterStoreModule from '@/hooks/useRegisterStoreModule'
import store from '../driverStoreModule'
import { VIOLATIONS_ACTIONS } from './violations-enums'
import snakeCaseToCamelCase from '@/utils/snake-case-to-camcel-caase'
import { errorNotification } from '@/utils/toast-notifications'
import BasePagination from '@/UI/BasePagination.vue'

export default {
  components: {
    BTable,
    BCard,
    BFormTextarea,
    BOverlay,
    BFormInput,
    BDropdown,
    BDropdownItem,
    BFormGroup,
    BRow,
    BCol,
    BButton,
    BasePagination,
    BFormSelect,
    BSpinner,
    BFormRadioGroup,
  },
  setup(props, ctx) {
    const { email: adminEmail } = JSON.parse(localStorage.getItem('userData'))
    useRegisterStoreModule('drivers', store)

    const tableColumns = [
      'violation_type',
      {
        label: 'state',
        key: 'state',
        formatter: val => snakeCaseToCamelCase(val),
      },
      {
        label: 'Source ID',
        key: 'description',
      },
      {
        label: 'Assigned by',
        key: 'assigned_by',
      },
      {
        label: 'Action By',
        key: 'action_by',
      },
      {
        label: 'created at',
        key: 'created_at',
        formatter: val => formatDate(val),
      },
      'actions',
    ]

    const filterValues = ref({
      violation_type: null,
      state: null,
      page: 1,
      description: null,
      per_page: 10,
    })
    const stateOptions = computed(() => {
      const arr = Object.keys(VIOLATIONS_ACTIONS)
      return arr.map(el => ({ label: snakeCaseToCamelCase(el), value: el }))
    })

    const violationTypes = ref([])
    const loadingViolationTypes = ref(false)
    const fetchViolationTypes = async () => {
      loadingViolationTypes.value = true
      const result = await ctx.root.$store.dispatch('drivers/fetchViolationTypes')
      const { data } = result.data
      violationTypes.value = data
      loadingViolationTypes.value = false
    }

    fetchViolationTypes()

    const violations = ref([])
    const totalViolations = ref(0)
    const loading = ref(false)
    const fetchViolations = async () => {
      loading.value = true
      // if (violationTypes.value.length === 0) {
      //   await fetchViolationTypes()
      // }
      const result = await ctx.root.$store.dispatch('drivers/fetchViolationsList', {
        violation_type: filterValues.value.violation_type,
        state: filterValues.value.state,
        page: filterValues.value.page,
        per_page: filterValues.value.per_page,
        description: filterValues.value.description || undefined,
      })
      const { total, violations: violationResult } = result.data.data.data
      totalViolations.value = total
      violations.value = violationResult.map(v => {
        if (v.violation_type) {
          const violationType = violationTypes.value.find(t => t.id === v.violation_type)
          const violationTypeName = violationType ? violationType.name : ''
          return { ...v, violation_type: violationTypeName }
        }
        return v
      })
      loading.value = false
    }

    const currentViewedViolation = ref()
    const availableTriggers = ref([])
    const fetchingTriggers = ref(false)
    const viewViolationTriggers = async record => {
      currentViewedViolation.value = JSON.parse(JSON.stringify(record))
      ctx.root.$bvModal.show('violation-triggers')
      fetchingTriggers.value = true
      const result = await ctx.root.$store.dispatch('drivers/fetchViolationTriggers', {
        violationId: record.id,
      })
      const { triggers } = result.data.data
      availableTriggers.value = triggers
      fetchingTriggers.value = false
    }

    const violationActionForm = ref({
      action: '',
      description: '',
      action_by: adminEmail,
    })
    const resetTriggersModalForm = () => {
      availableTriggers.value = []
      violationActionForm.value.action = ''
      violationActionForm.value.description = ''
    }

    const applyingAction = ref(false)
    const applyActionOnViolationHandler = async () => {
      applyingAction.value = true
      try {
        await ctx.root.$store.dispatch('drivers/applyViolationTrigger', {
          violationId: currentViewedViolation.value.id,
          body: violationActionForm.value,
        })
        ctx.root.$bvModal.hide('violation-triggers')
        fetchViolations()
      } catch (error) {
        errorNotification('An error occured..')
      } finally {
        applyingAction.value = false
      }
    }

    watch(() => filterValues.value.per_page, () => {
      fetchViolations()
    })

    const viewDriver = async violation => {
      try {
        loading.value = true
        const result = await ctx.root.$store.dispatch('drivers/fetchDriver', {
          id: violation.driver,
        })
        const { externalId } = result.data.data
        // eslint-disable-next-line no-restricted-globals
        window.open(`${location.origin}/drivers/${violation.driver}?externalId=${externalId}`)
      } catch (err) {
        errorNotification('Driver Not Found')
      } finally {
        loading.value = false
      }
    }

    const resetStateFilter = () => {
      filterValues.value.state = undefined
      fetchViolations()
    }

    const resetViolationTypeFilter = () => {
      filterValues.value.violation_type = undefined
      fetchViolations()
    }
    return {
      stateOptions,
      tableColumns,
      resetTriggersModalForm,
      fetchingTriggers,
      violationTypes,
      loadingViolationTypes,
      fetchViolationTypes,
      availableTriggers,
      loading,
      currentViewedViolation,
      applyingAction,
      violations,
      violationActionForm,
      totalViolations,
      viewViolationTriggers,
      filterValues,
      applyActionOnViolationHandler,
      fetchViolations,
      viewDriver,
      resetStateFilter,
      resetViolationTypeFilter,
    }
  },
}
</script>

    <style>

    </style>
